'use client';

import { useCallback, useMemo } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import { LeadCaptureForm } from '@selenos/leads/components/lead-capture-form';

import { buildTrackingParams } from '~/lib/tracking/tracking-params';

import { useVerticalConfig } from '../use-vertical-config';

interface SpitexInlineFormProps {
  side: 'supply' | 'demand';
  citySlug: string;
  verticalSlug: string;
  locale: string;
  submitAction: (data: Record<string, unknown>) => Promise<{
    success: boolean;
    leadId?: string;
    error?: string;
  }>;
  partialSaveAction?: (data: Record<string, unknown>) => Promise<{
    success: boolean;
    leadId?: string;
    error?: string;
  }>;
  /**
   * Optional pre-filled values for the vertical custom fields (Step 2).
   * Used by the Kostenrechner page to carry calculator inputs (serviceType,
   * canton, careDescription) into the lead form so the sales team sees the
   * context without the user having to re-enter it.
   */
  initialCustomFields?: Record<string, unknown>;
}

const formContent = {
  supply: {
    headline: 'Jetzt bewerben',
    subheadline:
      'Fuellen Sie das Formular aus. Es dauert weniger als 5 Minuten.',
  },
  demand: {
    headline: 'Ihre unverbindliche Anfrage',
    subheadline:
      'Beschreiben Sie Ihren Pflegebedarf. Wir melden uns innert einer Stunde bei Ihnen.',
  },
};

/**
 * Inline lead capture form for Spitex landing pages.
 *
 * Uses id="lead-form" to match the scroll targets in SpitexHero,
 * SpitexFinalCta, and CareTypeSelector.
 */
export function SpitexInlineForm({
  side,
  citySlug,
  verticalSlug,
  locale,
  submitAction,
  partialSaveAction,
  initialCustomFields,
}: SpitexInlineFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { verticalConfig, customFormFields, phonePlaceholder } =
    useVerticalConfig(verticalSlug);

  const content = formContent[side];

  const tracking = useMemo(
    () => buildTrackingParams((key) => searchParams.get(key)),
    [searchParams],
  );

  const handleSuccess = useCallback(
    (leadId?: string) => {
      router.push(
        `/${locale}/${verticalSlug}/${citySlug}/thank-you${leadId ? `?leadId=${leadId}` : ''}`,
      );
    },
    [locale, verticalSlug, citySlug, router],
  );

  if (!verticalConfig) {
    return null;
  }

  return (
    <section id="lead-form" className="w-full px-4 py-16 sm:py-20">
      <div className="mx-auto max-w-lg">
        <h2 className="text-foreground mb-2 text-center text-2xl font-bold sm:text-3xl">
          {content.headline}
        </h2>
        <p className="text-muted-foreground mb-8 text-center">
          {content.subheadline}
        </p>

        <div className="bg-card rounded-2xl border border-white/10 p-6 sm:p-8">
          <LeadCaptureForm
            verticalConfig={verticalConfig}
            citySlug={citySlug}
            verticalSlug={verticalSlug}
            customFormFields={customFormFields}
            submitAction={submitAction}
            partialSaveAction={partialSaveAction}
            onSuccess={handleSuccess}
            tracking={tracking}
            phonePlaceholder={phonePlaceholder}
            initialCustomFields={initialCustomFields}
          />
        </div>

        <p className="text-muted-foreground mt-4 text-center text-xs">
          Ihre Daten werden gemaess nDSG verarbeitet und nicht an Dritte
          weitergegeben.
        </p>
      </div>
    </section>
  );
}
